import React, { useState, useRef, useCallback, useEffect } from 'react';
import { parseExcelFile, OrderRow } from './parseOrders';
import { saveOrders, getHeaders, getOrderCount, loadRecentOrders, searchOrders, clearAllOrders } from './orderDB';

const PAGE_SIZE = 50;

function useColumnResize(headers: string[]) {
  const [colWidths, setColWidths] = useState<Record<number, number>>({});
  const resizing = useRef<{ col: number; startX: number; startW: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizing.current) return;
      const { col, startX, startW } = resizing.current;
      const diff = e.clientX - startX;
      setColWidths(prev => ({ ...prev, [col]: Math.max(50, startW + diff) }));
    };
    const onUp = () => {
      if (resizing.current) {
        resizing.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startResize = (colIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    const th = (e.target as HTMLElement).parentElement!;
    resizing.current = { col: colIndex, startX: e.clientX, startW: th.offsetWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const getWidth = (colIndex: number) => colWidths[colIndex];

  return { startResize, getWidth };
}

function OrderManagement() {
  const [headers, setHeaders] = useState<string[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [toast, setToast] = useState('');
  const [storedCount, setStoredCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // 프로그레스
  const [progress, setProgress] = useState({ current: 0, total: 0, fileName: '' });

  const { startResize, getWidth } = useColumnResize(headers);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  // 페이지 진입 시 헤더 + 건수 + 최근 N건 + 최근 업데이트 시간 로드
  useEffect(() => {
    Promise.all([
      getHeaders(),
      getOrderCount(),
      loadRecentOrders(PAGE_SIZE),
      fetch('/api/orders/last-updated', { headers: { Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}` } }).then(r => r.ok ? r.json() : { lastUpdated: null }),
    ])
      .then(([h, count, rows, { lastUpdated: lu }]) => {
        setHeaders(h);
        setStoredCount(count);
        setOrders(rows);
        setLastUpdated(lu);
        setInitialLoading(false);
      })
      .catch(() => setInitialLoading(false));
  }, []);

  // 검색 디바운스
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!searchTerm.trim()) {
      // 검색어 없으면 최근 N건
      setSearching(true);
      loadRecentOrders(displayCount).then(rows => {
        setOrders(rows);
        setSearching(false);
      });
      return;
    }

    setSearching(true);
    searchTimerRef.current = setTimeout(() => {
      searchOrders(searchTerm.trim(), 500).then(rows => {
        setOrders(rows);
        setSearching(false);
      });
    }, 300);
  }, [searchTerm, displayCount]);

  const processFiles = async (files: File[]) => {
    if (!password) {
      setError('비밀번호를 먼저 입력해주세요.');
      return;
    }

    const xlsxFiles = files.filter(f =>
      f.name.endsWith('.xlsx') && !f.name.startsWith('~$')
    );

    if (xlsxFiles.length === 0) {
      setError('xlsx 파일이 없습니다.');
      return;
    }

    setLoading(true);
    setError('');
    setProgress({ current: 0, total: xlsxFiles.length, fileName: '' });

    let parsedHeaders: string[] = headers;
    let successCount = 0;
    let failCount = 0;
    const BATCH_SIZE = 20;

    for (let i = 0; i < xlsxFiles.length; i += BATCH_SIZE) {
      const batch = xlsxFiles.slice(i, i + BATCH_SIZE);
      let batchRows: OrderRow[] = [];

      for (const file of batch) {
        setProgress({ current: i + batch.indexOf(file) + 1, total: xlsxFiles.length, fileName: file.name });

        try {
          const result = await parseExcelFile(file, password);
          if (result.rows.length === 0) continue; // 빈 파일 스킵
          batchRows = batchRows.concat(result.rows);
          if (result.headers.length > 0) parsedHeaders = result.headers;
          successCount++;
        } catch {
          continue; // 실패 시 바로 다음 파일
        }
      }

      if (batchRows.length > 0) {
        await saveOrders(batchRows, parsedHeaders);
      }

      await new Promise(r => setTimeout(r, 0));
    }

    // 완료 후 갱신
    const [h, count, rows, { lastUpdated: lu }] = await Promise.all([
      getHeaders(), getOrderCount(), loadRecentOrders(PAGE_SIZE),
      fetch('/api/orders/last-updated', { headers: { Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}` } }).then(r => r.ok ? r.json() : { lastUpdated: null }),
    ]);
    setHeaders(h);
    setStoredCount(count);
    setOrders(rows);
    setLastUpdated(lu);
    setDisplayCount(PAGE_SIZE);
    setSearchTerm('');
    setLoading(false);
    setProgress({ current: 0, total: 0, fileName: '' });

    if (failCount > 0) {
      setError(`${failCount}개 파일 처리 실패 (비밀번호가 다르거나 형식이 다른 파일)`);
    }
    if (successCount > 0) {
      alert(`업로드 완료\n\n처리 파일: ${successCount}개\n총 저장 주문: ${count.toLocaleString()}건${failCount > 0 ? `\n실패: ${failCount}개` : ''}`);
    }
  };

  const handleFileInput = (files: FileList | null) => {
    if (!files) return;
    processFiles(Array.from(files));
  };

  const handleClear = async () => {
    if (!window.confirm('저장된 모든 주문 데이터를 삭제하시겠습니까?')) return;
    await clearAllOrders();
    setOrders([]);
    setHeaders([]);
    setStoredCount(0);
    setDisplayCount(PAGE_SIZE);
    showToast('모든 데이터가 삭제되었습니다');
  };

  const loadMore = () => {
    const next = displayCount + PAGE_SIZE;
    setDisplayCount(next);
  };

  const progressPct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  if (initialLoading) {
    return (
      <div>
        <h1 className="text-2xl font-light text-gray-800 mb-6">고객 주문관리</h1>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-400">
          데이터 불러오는 중...
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-800 mb-6">고객 주문관리</h1>

      {/* 업로드 영역 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-end gap-4 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">엑셀 비밀번호</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호 입력"
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown w-48"
            />
          </div>
          <div className="text-sm text-gray-500">
            저장된 주문: <strong className="text-gray-800">{storedCount.toLocaleString()}건</strong>
          </div>
          {lastUpdated && (
            <div className="text-sm text-gray-400">
              최근업데이트일 : {new Date(lastUpdated).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(/\. /g, '.').replace('.', '.').replace(',', '')}
            </div>
          )}
          {storedCount > 0 && (
            <button
              onClick={handleClear}
              className="px-3 py-2 text-xs border border-gray-300 text-gray-500 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
            >전체 삭제</button>
          )}
        </div>

        {/* 업로드 영역 */}
        {!loading ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileInput(e.dataTransfer.files); }}
            className={`relative flex flex-col items-center justify-center py-10 border-2 border-dashed rounded-xl transition-all ${
              dragOver ? 'border-jeju-brown bg-jeju-brown/5' : 'border-gray-300'
            }`}
          >
            <div className="text-3xl mb-3 opacity-30">📊</div>
            <p className="text-sm text-gray-400 mb-4">엑셀 파일을 드래그하거나 아래 버튼을 사용하세요</p>
            <div className="flex gap-3">
              <button
                onClick={() => folderInputRef.current?.click()}
                className="px-5 py-2.5 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:bg-jeju-brown-dark transition-colors"
              >폴더 선택 (일괄 등록)</button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >파일 선택</button>
            </div>
            <p className="text-xs text-gray-300 mt-3">폴더 선택 시 하위 파일을 모두 자동 등록합니다</p>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".xlsx,.xls"
              onChange={(e) => { handleFileInput(e.target.files); e.target.value = ''; }}
              className="hidden"
            />
            <input
              ref={folderInputRef}
              type="file"
              // @ts-ignore
              webkitdirectory=""
              // @ts-ignore
              directory=""
              multiple
              onChange={(e) => { handleFileInput(e.target.files); e.target.value = ''; }}
              className="hidden"
            />
          </div>
        ) : (
          <div className="py-10 px-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                처리 중... {progress.current.toLocaleString()} / {progress.total.toLocaleString()}
              </span>
              <span className="text-sm font-semibold text-jeju-brown">{progressPct}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-jeju-brown h-3 rounded-full transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-2 truncate" title={progress.fileName}>
              {progress.fileName}
            </p>
          </div>
        )}

        {error && (
          <p className="text-red-500 text-sm mt-3">{error}</p>
        )}
      </div>

      {/* 주문 테이블 */}
      {storedCount > 0 && headers.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* 검색 + 통계 */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="검색..."
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown w-80"
              />
              {searching && <span className="text-sm text-gray-400">검색 중...</span>}
              {!searching && searchTerm && (
                <span className="text-sm text-gray-400">{orders.length.toLocaleString()}건 검색됨</span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {searchTerm
                ? `${orders.length.toLocaleString()}건 / 전체 ${storedCount.toLocaleString()}건`
                : `최근 ${orders.length.toLocaleString()}건 표시 / 전체 ${storedCount.toLocaleString()}건`
              }
            </div>
          </div>

          {/* 테이블 */}
          <div className="overflow-auto max-h-[calc(100vh-420px)]">
            <table className="text-sm" style={{ tableLayout: 'fixed' }}>
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {headers.map((h, i) => (
                    <th
                      key={i}
                      className="text-left px-3 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap border-b border-gray-200 relative group"
                      style={{ width: getWidth(i) || 'auto', minWidth: 50 }}
                    >
                      {h}
                      <div
                        onMouseDown={(e) => startResize(i, e)}
                        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-jeju-brown/30 group-hover:bg-gray-300"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map((o) => (
                  <tr key={String(o['상품주문번호'])} className="hover:bg-gray-50 transition-colors">
                    {headers.map((h, i) => (
                      <td
                        key={i}
                        className="px-3 py-2.5 text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis"
                        style={{ width: getWidth(i) || 'auto', maxWidth: getWidth(i) || 250 }}
                        title={String(o[h] ?? '')}
                      >
                        {String(o[h] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 더 보기 */}
          {!searchTerm && orders.length < storedCount && (
            <div className="px-6 py-4 border-t border-gray-200 text-center">
              <button
                onClick={loadMore}
                className="px-6 py-2 text-sm text-jeju-brown border border-jeju-brown rounded-lg hover:bg-jeju-brown hover:text-white transition-colors"
              >더 보기 ({Math.min(PAGE_SIZE, storedCount - orders.length).toLocaleString()}건)</button>
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

export default OrderManagement;
